import { QueuedToast, ToastState, ToastStateProps } from "@react-stately/toast";
import { NotificationContentType } from "ds/ui";
import { uniqueId } from "lodash";
import { useCallback, useMemo, useReducer } from "react";

export const NOTIFICATION_ACTIONS = {
  ADD: "ADD",
  CLOSE: "CLOSE",
};

type NotificationAction =
  | {
      payload: QueuedToast<NotificationContentType>;
      type: typeof NOTIFICATION_ACTIONS.ADD;
    }
  | {
      payload: QueuedToast<NotificationContentType>["key"];
      type: typeof NOTIFICATION_ACTIONS.CLOSE;
    };

export const shouldBlockDuplicateNotification = (
  state: QueuedToast<NotificationContentType>[],
  action: NotificationAction,
): boolean => {
  if (
    typeof action.payload === "string" ||
    !action.payload.content.blockDuplicateNotificationTypes
  ) {
    return false;
  }

  const notificationContent = action.payload.content;

  return state.some(
    (notification) => notification.content.type === notificationContent.type,
  );
};

const notificationReducer = (
  state: QueuedToast<NotificationContentType>[],
  action: NotificationAction,
): QueuedToast<NotificationContentType>[] => {
  switch (action.type) {
    case NOTIFICATION_ACTIONS.ADD: {
      if (shouldBlockDuplicateNotification(state, action)) {
        return state;
      }
      return [action.payload as QueuedToast<NotificationContentType>, ...state];
    }
    case NOTIFICATION_ACTIONS.CLOSE: {
      return state.filter(
        (notification) => notification.key !== action.payload,
      );
    }
    default: {
      return state;
    }
  }
};

const notImplemeted = () => {};

export const useNotificationState = (
  props?: Pick<ToastStateProps, "maxVisibleToasts">,
): ToastState<NotificationContentType> => {
  const [notifications, dispatch] = useReducer(notificationReducer, []);

  const visibleToasts = useMemo(
    () =>
      props?.maxVisibleToasts
        ? notifications.slice(0, props.maxVisibleToasts)
        : notifications,
    [props?.maxVisibleToasts, notifications],
  );

  const add = useCallback(
    (notification: NotificationContentType) => {
      const key = uniqueId("notification");
      dispatch({
        payload: { animation: "entering", content: notification, key },
        type: NOTIFICATION_ACTIONS.ADD,
      });
      return key;
    },
    [dispatch],
  );

  const close = useCallback(
    (key: string) =>
      dispatch({
        payload: key,
        type: NOTIFICATION_ACTIONS.CLOSE,
      }),
    [dispatch],
  );

  return {
    add,
    close,
    pauseAll: notImplemeted,
    // required to meet react-aria ToastState
    remove: notImplemeted,
    resumeAll: notImplemeted,
    visibleToasts,
  };
};
